/* Bells of Steel: accessibility behaviours (WCAG 2.2 AA).
   Scope: (a) keyboard behaviour the theme is missing, (b) band-aids for markup that
   third-party apps inject and we can't edit (each marked APP). Anything we CAN fix in
   Liquid is fixed there (see CHANGES.md); this file must stay safe to run twice. */
(() => {
  if (window.__bosA11y) return;
  window.__bosA11y = true;

  const onReady = (fn) => (document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', fn) : fn());
  const isVisible = (el) => !!el && el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden';

  /* ------------------------------------------------------------------ *
   * 1. APP (Selleasy / "LB Upsell"): Add to cart and Checkout do nothing
   *    when activated with Enter or Space (2.1.1). Selleasy cancels the
   *    keyboard-generated click and never replays it. A programmatic
   *    click() goes through its normal path, so replay the key as one.
   * ------------------------------------------------------------------ */
  document.addEventListener('keydown', (e) => {
    if ((e.key !== 'Enter' && e.key !== ' ') || e.repeat || e.altKey || e.ctrlKey || e.metaKey) return;
    const btn = e.target.closest && e.target.closest('button[lb-atc-listener], button[lb-checkout-listener], [lb-atc-listener] button[type=submit]');
    if (!btn || btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
    e.preventDefault();
    btn.click();
  }, true);

  /* ------------------------------------------------------------------ *
   * 2. Main navigation (2.1.1, 4.1.2, 1.3.1)
   *    Liquid change moves the parent link out of <summary> (CHANGES.md §3):
   *    the label is a <span class="main-nav__item ..."> and the URL sits in
   *    summary[data-href]. Pointer users keep "click RACKS to go to
   *    /collections/racks"; keyboard users get Enter/Space = open, Escape =
   *    close and return focus, plus a "Shop all Racks" link that appears on
   *    focus as the first item inside the panel.
   *    Old markup (<a> still inside <summary>, no data-href, e.g. the live
   *    theme) is left to behave as it does today; Escape and aria-expanded
   *    below work with both.
   * ------------------------------------------------------------------ */
  // The summary[data-href] a pointer click should navigate from, or null. Only clicks that land on
  // the label (where the old <a> sat) navigate. On touch screens and in the mobile drawer the theme
  // turns pointer-events off on .main-nav__item-content (main.css), so those taps hit the <summary>
  // itself and open the submenu, as they always have. Screen-reader "press" targets the <summary>
  // too, so it opens the menu rather than leaving the page.
  const navLinkSummary = (e) => {
    const summary = e.target.closest && e.target.closest('summary[data-href]');
    if (!summary || !summary.dataset.href) return null;
    const label = e.target.closest('.main-nav__item');
    return label && label !== summary && summary.contains(label) ? summary : null;
  };
  document.addEventListener('click', (e) => {
    // detail 0 = keyboard activation (Enter/Space): let the theme open the panel.
    if (e.detail === 0 || e.button !== 0) return;
    const summary = navLinkSummary(e);
    if (!summary) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    // Modifier clicks open a new tab/window, as they did on the old link.
    if (e.metaKey || e.ctrlKey || e.shiftKey) window.open(summary.dataset.href, '_blank', 'noopener');
    else window.location.href = summary.dataset.href;
  }, true);
  document.addEventListener('auxclick', (e) => {
    if (e.button !== 1) return; // middle click = open in new tab, as on a link
    const summary = navLinkSummary(e);
    if (!summary) return;
    e.preventDefault();
    window.open(summary.dataset.href, '_blank', 'noopener');
  }, true);

  // Theme main.js: hovering a top-level item calls trapFocus(<summary>). With no focusable element
  // inside the summary any more, its Shift+Tab handler calls .focus() on undefined after cancelling
  // the key, so Shift+Tab would be stuck (2.1.2). On hover devices that hover trap is the only trap
  // set on a summary (keyboard toggles skip trapFocus there), so let Shift+Tab leave normally.
  const hoverDesktop = window.matchMedia('(hover: hover) and (min-width: 769px)');
  window.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab' || !e.shiftKey || !hoverDesktop.matches) return;
    const t = e.target;
    if (t && t.matches && t.matches('.main-nav summary[data-href]') &&
      !t.querySelector('a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])')) {
      e.stopPropagation();
    }
  }, true);

  const TOP_NAV = '.main-nav > li > details';
  const closeNavDisclosure = (details) => {
    if (!details) return;
    const summary = details.querySelector(':scope > summary');
    const menu = details.closest('main-menu');
    if (menu && typeof menu.close === 'function') {
      // Use the theme's own close so its overlay / focus-trap state stays consistent.
      menu.close(details, false);
      if (menu.overlayOpen && typeof menu.toggleOverlay === 'function') menu.toggleOverlay(false);
      menu.childNavOpen = false;
    } else {
      details.classList.remove('is-open');
      details.removeAttribute('open');
    }
    // Mobile drawer: undo what the theme's "Back" button undoes, or the top level can't scroll.
    const content = details.closest('.main-menu__content');
    if (content) content.classList.remove('main-menu__content--no-focus');
    if (summary) { summary.setAttribute('aria-expanded', 'false'); summary.focus(); }
  };
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    // Always the top-level panel: second-level <details> in mega menus are permanently open on
    // desktop (nav-menu.js-mega-nav), and closing one would blank that column.
    const openNav = (e.target.closest && e.target.closest(TOP_NAV)) ||
      document.querySelector(`${TOP_NAV}.is-open, ${TOP_NAV}[open].js-mega-nav`);
    if (!openNav || !(openNav.open || openNav.classList.contains('is-open'))) return;
    if (openNav.contains(document.activeElement) || openNav.classList.contains('is-open')) {
      e.preventDefault();
      closeNavDisclosure(openNav);
    }
  });
  const syncExpanded = (root = document) => {
    root.querySelectorAll('.main-nav details > summary').forEach((s) => {
      const d = s.parentElement;
      s.setAttribute('aria-expanded', d.open || d.classList.contains('is-open') ? 'true' : 'false');
    });
  };
  document.addEventListener('toggle', (e) => { if (e.target.matches && e.target.matches('.main-nav details')) syncExpanded(); }, true);

  /* ------------------------------------------------------------------ *
   * 3. Accordions rendered as <div data-accordion> (2.1.1, 4.1.2)
   *    Liquid should render a <button> (CHANGES.md §5); this keeps any
   *    remaining div toggles operable.
   * ------------------------------------------------------------------ */
  const enhanceAccordions = (root = document) => {
    root.querySelectorAll('tab-accordion:not([data-init="false"]) [data-accordion]:not([data-bos])').forEach((t) => {
      t.setAttribute('data-bos', '');
      if (t.tagName === 'BUTTON') return;
      const id = t.dataset.accordion;
      const panel = t.closest('tab-accordion').querySelector(`[data-accordion-content="${CSS.escape(id)}"]`);
      if (panel && !panel.id) panel.id = `bos-acc-${id}`;
      t.setAttribute('role', 'button');
      t.setAttribute('tabindex', '0');
      if (panel) t.setAttribute('aria-controls', panel.id);
      const sync = () => t.setAttribute('aria-expanded', panel && panel.offsetHeight > 0 ? 'true' : 'false');
      sync();
      t.addEventListener('click', () => setTimeout(sync, 550));
      t.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); t.click(); } });
    });
  };

  /* ------------------------------------------------------------------ *
   * 4. Carousels (2.1.1, 2.5.7, 1.3.1)
   *    Where a slider overflows but has no visible arrows, add Previous /
   *    Next buttons, and make scroll-only strips reachable by keyboard.
   * ------------------------------------------------------------------ */
  const arrow = (d) => `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="${d === 'prev' ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7'}" fill="none" stroke="currentColor" stroke-width="2"/></svg>`;
  const sectionName = (el) => {
    const sec = el.closest('.shopify-section, section');
    const h = sec && sec.querySelector('h2, h3, .section__heading, .h2');
    return (h && h.textContent.trim().replace(/\s+/g, ' ').slice(0, 60)) || 'Carousel';
  };
  const enhanceCarousels = () => {
    document.querySelectorAll('carousel-slider .slider, .slider.slider--contained').forEach((slider) => {
      if (slider.dataset.bos) return;
      if (!isVisible(slider)) return;
      const overflow = slider.scrollWidth > slider.clientWidth + 4;
      if (!overflow) return;
      slider.dataset.bos = '1';
      const host = slider.closest('carousel-slider') || slider.parentElement;
      const nav = host.querySelector('.slider-nav');
      const navVisible = nav && [...nav.querySelectorAll('button')].some(isVisible);
      const hasFocusable = !!slider.querySelector('a[href], button, input, select, [tabindex]:not([tabindex="-1"])');
      if (!hasFocusable) {
        // Scroll-only strip (e.g. "Free shipping / Trusted since 2010"): make it a labelled, focusable region.
        slider.setAttribute('tabindex', '0');
        // Keep a role the Liquid already set (the benefit strip uses role=group so repeated strips don't need unique landmark names)
        if (!slider.hasAttribute('role')) slider.setAttribute('role', 'region');
        if (!slider.hasAttribute('aria-label')) slider.setAttribute('aria-label', sectionName(slider));
      }
      if (navVisible) return;
      const label = sectionName(slider);
      const bar = document.createElement('div');
      bar.className = 'bos-carousel-nav';
      const mk = (dir) => {
        const b = document.createElement('button');
        b.type = 'button'; b.className = 'bos-carousel-btn'; b.dataset.dir = dir;
        b.setAttribute('aria-label', `${dir === 'prev' ? 'Previous' : 'Next'}: ${label}`);
        b.innerHTML = arrow(dir); // static SVG only
        bar.appendChild(b);
        return b;
      };
      const prev = mk('prev');
      const next = mk('next');
      slider.insertAdjacentElement('afterend', bar);
      host.classList.add('bos-carousel');
      const step = () => {
        const item = slider.querySelector('.slider__item, li, [class*="slide"]');
        return item ? item.getBoundingClientRect().width + 12 : slider.clientWidth * 0.8;
      };
      const update = () => {
        prev.disabled = slider.scrollLeft <= 2;
        next.disabled = slider.scrollLeft + slider.clientWidth >= slider.scrollWidth - 2;
      };
      prev.addEventListener('click', () => slider.scrollBy({ left: -step(), behavior: 'smooth' }));
      next.addEventListener('click', () => slider.scrollBy({ left: step(), behavior: 'smooth' }));
      slider.addEventListener('scroll', () => { clearTimeout(slider.__bosT); slider.__bosT = setTimeout(update, 80); }, { passive: true });
      update();
    });
  };

  /* ------------------------------------------------------------------ *
   * 5. APP markup we can't edit: names, roles, labels (1.1.1, 1.3.1, 4.1.2)
   * ------------------------------------------------------------------ */
  const fixAppMarkup = (root = document) => {
    // Searchanise mobile search field has no label
    root.querySelectorAll('#snize-mobile-search-input:not([aria-label])').forEach((i) => i.setAttribute('aria-label', 'Search products'));
    // Searchanise results: listbox wrapping a status + inner listbox
    root.querySelectorAll('#snize-instant-search-results[role="listbox"]').forEach((el) => { if (el.querySelector('[role="listbox"]')) el.removeAttribute('role'); });
    // Custom search "empty" panel declares listbox but contains plain links
    root.querySelectorAll('.bos-search-empty[role="listbox"]').forEach((el) => { if (!el.querySelector('[role="option"]')) { el.removeAttribute('role'); el.setAttribute('role', 'group'); } });
    // Pro Blogger related-products slider: <button role="presentation">
    root.querySelectorAll('.owl-prev[role="presentation"], .owl-next[role="presentation"]').forEach((b) => {
      b.removeAttribute('role');
      b.setAttribute('aria-label', b.classList.contains('owl-prev') ? 'Previous products' : 'Next products');
    });
    // Blog tables wrapped for horizontal scroll
    root.querySelectorAll('.scrollable-table:not([tabindex])').forEach((t) => {
      if (t.scrollWidth <= t.clientWidth + 2 || t.querySelector('a[href], button')) return;
      t.setAttribute('tabindex', '0'); t.setAttribute('role', 'region');
      const cap = t.querySelector('caption');
      let prevH = t.previousElementSibling; while (prevH && !/^H[2-4]$/.test(prevH.tagName)) prevH = prevH.previousElementSibling;
      const n = document.querySelectorAll('.scrollable-table[role="region"]').length + 1;
      t.setAttribute('aria-label', cap ? cap.textContent.trim() : prevH ? `Table: ${prevH.textContent.trim().slice(0, 60)}` : `Scrollable table ${n}`);
    });
    // Embedded videos / iframes without a title
    root.querySelectorAll('iframe:not([title])').forEach((f) => {
      const src = f.getAttribute('src') || '';
      if (/youtube|youtu\.be/.test(src)) f.title = 'YouTube video';
      else if (/vimeo/.test(src)) f.title = 'Vimeo video';
      else if (/web-pixels|about:blank/.test(src) || f.getBoundingClientRect().width < 2) { f.setAttribute('aria-hidden', 'true'); f.setAttribute('tabindex', '-1'); f.title = 'Tracking frame'; }
    });
    // Selleasy quantity steppers are divs with role=button but no Space support
    root.querySelectorAll('.lb-qty-add[role="button"]:not([data-bos]), .lb-qty-remove[role="button"]:not([data-bos])').forEach((d) => {
      d.setAttribute('data-bos', '');
      d.addEventListener('keydown', (e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); d.click(); } });
    });
    // Theme quantity inputs without a label (variant tables, cart). Liquid fix in CHANGES.md §6.
    root.querySelectorAll('input.qty-input__input:not([aria-label]):not([id])').forEach((i) => {
      const row = i.closest('tr, li, .variant-row, [data-variant-id], .card, .qty-row');
      const name = row && row.querySelector('.variant-title, .product-title, td, .title');
      i.setAttribute('aria-label', `Quantity${name ? ` for ${name.textContent.trim().replace(/\s+/g, ' ').slice(0, 60)}` : ''}`);
    });
  };

  /* ------------------------------------------------------------------ *
   * 6. Dialogs/drawers: return focus to the opener on close (2.4.3)
   * ------------------------------------------------------------------ */
  let lastOpener = null;
  document.addEventListener('click', (e) => { const b = e.target.closest && e.target.closest('button, a[href], summary'); if (b) lastOpener = b; }, true);
  const watchDrawer = (d) => {
    new MutationObserver(() => {
      const open = d.hasAttribute('open') || d.classList.contains('is-open');
      if (!open && d.contains(document.activeElement) && lastOpener && document.contains(lastOpener)) lastOpener.focus();
    }).observe(d, { attributes: true, attributeFilter: ['open', 'class'] });
  };

  /* ------------------------------------------------------------------ *
   * 7. APP (Gorgias widget on mobile): rewrites the viewport meta to
   *    maximum-scale=2, which caps pinch-zoom (1.4.4). Put it back.
   * ------------------------------------------------------------------ */
  const fixViewport = () => {
    const m = document.querySelector('meta[name="viewport"]');
    if (!m) return;
    const c = m.getAttribute('content') || '';
    if (/maximum-scale|user-scalable\s*=\s*(no|0)/i.test(c)) m.setAttribute('content', 'width=device-width, initial-scale=1');
  };
  fixViewport();
  new MutationObserver(fixViewport).observe(document.head, { subtree: true, childList: true, attributes: true, attributeFilter: ['content'] });

  /* ------------------------------------------------------------------ *
   * 8. Header search (theme predictive-search + APP Searchanise dropdown):
   *    the overlay and results stay open after focus leaves the field and
   *    cover the header/page (2.4.11), and Escape does nothing once focus
   *    has moved on (1.4.13 dismissible). Close both when focus leaves;
   *    Escape closes and returns focus to the field.
   * ------------------------------------------------------------------ */
  const snize = () => document.getElementById('snize-instant-search-results');
  const closeSearch = (refocus) => {
    document.querySelectorAll('predictive-search[open]').forEach((ps) => { if (typeof ps.close === 'function') ps.close(); });
    document.querySelectorAll('.js-search-overlay.is-visible').forEach((o) => o.classList.remove('is-visible'));
    const r = snize(); if (r && r.style.display !== 'none') r.style.display = 'none';
    const input = document.getElementById('header-search');
    if (input) input.setAttribute('aria-expanded', 'false');
    if (refocus && input) input.focus();
  };
  const inSearch = (el) => !!el && !!(el.closest && (el.closest('predictive-search, .search, form[role="search"]') || (snize() && snize().contains(el))));
  document.addEventListener('focusin', (e) => {
    const open = document.querySelector('predictive-search[open], .js-search-overlay.is-visible') || (snize() && isVisible(snize()));
    if (open && !inSearch(e.target)) closeSearch(false);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const open = document.querySelector('predictive-search[open], .js-search-overlay.is-visible') || (snize() && isVisible(snize()));
    if (open) { closeSearch(inSearch(document.activeElement)); }
  });
  // Searchanise re-shows its own dropdown on the next keystroke; clear our inline hide when typing resumes.
  document.addEventListener('input', (e) => { if (e.target && e.target.id === 'header-search') { const r = snize(); if (r) r.style.display = ''; } }, true);

  /* ------------------------------------------------------------------ */
  // WAVE "Broken ARIA reference": aria-labelledby / aria-controls pointing at an id that doesn't
  // exist (yet). Park the reference in data-bos-<attr> while its target is missing and put it
  // back as soon as the target appears (e.g. Searchanise results, drawers rendered on demand).
  // The real fix for the featured-collection "tab-3" panel is in CHANGES.md §14.
  const ARIA_REFS = ['aria-labelledby', 'aria-describedby', 'aria-controls', 'aria-owns'];
  const fixAriaRefs = (root) => {
    root.querySelectorAll(ARIA_REFS.map((a) => `[${a}],[data-bos-${a}]`).join(',')).forEach((el) => {
      ARIA_REFS.forEach((a) => {
        const live = el.getAttribute(a), parked = el.getAttribute('data-bos-' + a);
        const all = [...new Set(((live || '') + ' ' + (parked || '')).split(/\s+/).filter(Boolean))];
        if (!all.length) return;
        const ok = all.filter((id) => document.getElementById(id));
        const missing = all.filter((id) => !document.getElementById(id));
        if (ok.length) el.setAttribute(a, ok.join(' ')); else el.removeAttribute(a);
        if (missing.length) el.setAttribute('data-bos-' + a, missing.join(' ')); else el.removeAttribute('data-bos-' + a);
      });
    });
  };

  // WAVE "Missing alternative text" on the local store pages (showroom photos), the geo popup
  // and similar custom sections. Fallback only: real alt text belongs in the image settings (CHANGES §14).
  // Meaningful photos get "<section heading> photo"; images inside a link or button that already
  // has text, and the geo-popup art, are marked decorative (alt="").
  const fixMissingAlt = (root) => {
    root.querySelectorAll('img:not([alt])').forEach((img) => {
      if (img.closest('.bos-geo-popup, a[aria-label], button[aria-label]')) { img.setAttribute('alt', ''); return; }
      const ctl = img.closest('a, button');
      if (ctl && ctl.textContent.trim()) { img.setAttribute('alt', ''); return; }
      const sec = img.closest('[id^="shopify-section"], section');
      const h = sec && sec.querySelector('h1, h2, h3');
      const label = h ? h.textContent.replace(/\s+/g, ' ').trim().slice(0, 80) : '';
      img.setAttribute('alt', label ? label + ' photo' : '');
    });
    // "Get Directions" is a <button href> (not a link): make it work as a link for keyboard/AT.
    root.querySelectorAll('button[href]:not([data-bos-link])').forEach((b) => {
      b.setAttribute('data-bos-link', ''); b.setAttribute('role', 'link');
      if (!b.getAttribute('type')) b.setAttribute('type', 'button');   // never submits a surrounding form
      b.addEventListener('click', (e) => { e.preventDefault(); window.open(b.getAttribute('href'), '_blank', 'noopener'); });
    });
    // Links with no text (e.g. image-only badge links): name them from the destination.
    root.querySelectorAll('a[href]').forEach((a) => {
      if (a.textContent.trim() || a.getAttribute('aria-label') || a.querySelector('img[alt]:not([alt=""]), svg[aria-label]')) return;
      const host = (() => { try { return new URL(a.href).hostname.replace(/^www\./, ''); } catch (_) { return ''; } })();
      const newTab = a.target === '_blank' ? ' (opens new tab)' : '';
      a.setAttribute('aria-label', (/google/.test(host) ? 'Bells of Steel on Google' : 'Link to ' + host) + newTab);
    });
  };

  // Empty headings (e.g. About the Founder media grid block with no title) are fixed in Liquid
  // (CHANGES.md §14), not here: swapping elements at runtime could swallow headings that an
  // app fills in a moment later.

  const runAll = (root) => {
    try { fixAriaRefs(root); } catch (_) {}
    try { fixMissingAlt(root); } catch (_) {}
    enhanceAccordions(root); fixAppMarkup(root); syncExpanded();
  };
  onReady(() => {
    runAll(document);
    document.querySelectorAll('cart-drawer, side-drawer, quick-add-drawer').forEach(watchDrawer);
    // Carousels size themselves after lazy init; check after load and on resize.
    const carousels = () => { try { enhanceCarousels(); } catch (_) { /* never break the page */ } };
    window.addEventListener('load', () => setTimeout(carousels, 600));
    setTimeout(carousels, 2500);
    let rt; window.addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(carousels, 300); });
    // Apps render late (Searchanise, Okendo, Selleasy, Klaviyo): re-run on DOM changes,
    // debounced so bursts of DOM work cost one pass.
    let t = null;
    new MutationObserver(() => {
      clearTimeout(t);
      t = setTimeout(() => { try { runAll(document); } catch (_) {} }, 150);
    }).observe(document.body, { childList: true, subtree: true });
  });
})();
